import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronRight, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getProfilePictureUrl } from '@/utils/profileUtils';

interface FeaturedTutorsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tutors: any[];
  isLoading: boolean;
}

const FeaturedTutorsModal = ({ open, onOpenChange, tutors, isLoading }: FeaturedTutorsModalProps) => {
  const navigate = useNavigate();

  const getTutorName = (tutor: any) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Featured Tutor';
  };

  const handleTutorClick = (tutorId: string) => {
    onOpenChange(false);
    navigate(`/tutor/${tutorId}`);
  };

  const handleViewAll = () => {
    onOpenChange(false);
    navigate('/tutors');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Featured Tutors</DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3 p-3 rounded-lg animate-pulse">
                  <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : tutors.length > 0 ? (
            <>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {tutors.map((tutor) => (
                  <div 
                    key={tutor.id} 
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-md"
                    onClick={() => handleTutorClick(tutor.id)}
                  >
                    <Avatar className="h-12 w-12">
                      <AvatarImage 
                        src={getProfilePictureUrl(tutor)} 
                        alt={getTutorName(tutor)}
                      />
                      <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-medium">
                        {getTutorName(tutor).charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getTutorName(tutor)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {tutor.subjects?.[0] || 'Expert Tutor'}
                      </p>
                      {tutor.hourlyRate && (
                        <p className="text-xs text-green-600 font-medium">
                          ${tutor.hourlyRate}/hr
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                ))}
              </div>
              
              <Button
                onClick={handleViewAll}
                variant="outline"
                className="w-full mt-4"
              >
                View All Tutors
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm mb-4">No featured tutors yet</p>
              <Button
                onClick={handleViewAll}
                variant="outline"
              >
                Browse All Tutors
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeaturedTutorsModal;
